import { NavLink, Outlet, Link, useLocation } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { useRegion } from '../hooks/useRegion';
import { HeaderAuthLink } from '../components/HeaderAuthLink';
import { CookieConsentMount } from '../cookies/CookieConsentMount';
import { openCookiePreferences } from '../cookies/openCookiePreferences';

/**
 * Primary navigation — per Bug #8199 (nav-order-and-pricing-page-redefinition-
 * 2026-05-13). Buyer journey: capability → product → cost-shape → trust → convert.
 *
 *   Home | Services ▾ | Platform | Pricing | Insights | About | Contact
 *
 * Services dropdown reveals the four FY27 product lines (Managed IT,
 * Platform Builds, AI Enablement, Marketing Technology). Click Services
 * to land on /services overview; hover or click the chevron to expand.
 */

type LeafNavItem = { path: string; label: string; end?: boolean; kind?: 'leaf' };
type DropdownNavItem = {
  label: string;
  path: string; // parent landing page
  kind: 'dropdown';
  children: { path: string; label: string }[];
};
type NavItem = LeafNavItem | DropdownNavItem;

const navItems: NavItem[] = [
  { path: '/', label: 'Home', end: true },
  {
    label: 'Services',
    path: '/services',
    kind: 'dropdown',
    children: [
      { path: '/services/managed-it', label: 'Managed IT' },
      { path: '/services/platform-builds', label: 'Platform Builds' },
      { path: '/ai-enablement', label: 'AI Enablement' },
      { path: '/services/martech', label: 'Marketing Technology' },
    ],
  },
  { path: '/platform', label: 'Platform' },
  { path: '/pricing', label: 'Pricing' },
  { path: '/insights', label: 'Insights' },
  { path: '/about', label: 'About' },
  { path: '/contact', label: 'Contact' },
];

export function PublicLayout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const region = useRegion();

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:bg-fern-600 focus:text-white focus:px-3 focus:py-2 focus:rounded-md focus:z-50"
      >
        Skip to content
      </a>

      {/* Top contact bar — per F1-14A § 7.1. Hidden below md to keep mobile compact. */}
      <div className="hidden md:block bg-fern-700 text-sand-100 text-xs">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-1.5 sm:px-6 lg:px-8">
          <div className="flex items-center gap-5">
            <a href={`mailto:${region.email}`} className="hover:text-white">
              {region.email}
            </a>
            {region.phone.tel ? (
              <a href={`tel:${region.phone.tel}`} className="hover:text-white">
                {region.phone.display}
              </a>
            ) : (
              <span>{region.phone.display}</span>
            )}
            <span className="text-sand-200/80">Office hours: {region.hours}</span>
          </div>
          <nav aria-label="Social" className="flex items-center gap-3">
            <a
              href="https://www.facebook.com/conscierra"
              aria-label="Facebook"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-white"
            >
              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" />
              </svg>
            </a>
            <a
              href="https://www.linkedin.com/company/conscierra"
              aria-label="LinkedIn"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-white"
            >
              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.063 2.063 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
              </svg>
            </a>
            <a
              href="https://www.instagram.com/conscierra"
              aria-label="Instagram"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-white"
            >
              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
              </svg>
            </a>
          </nav>
        </div>
      </div>

      <header className="sticky top-0 z-40 border-b border-sand-200 bg-white/95 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
          <Link to="/" aria-label="Conscierra home" className="flex items-center">
            <img
              src="/brand/conscierra-logo-fern.png"
              alt="Conscierra"
              className="h-8 w-auto md:h-10"
              width={183}
              height={59}
            />
          </Link>

          <nav aria-label="Primary" className="hidden md:flex items-center gap-1">
            {navItems.map((item) =>
              item.kind === 'dropdown' ? (
                <NavDropdown key={item.label} item={item} />
              ) : (
                <NavLink
                  key={item.path}
                  to={item.path}
                  end={item.end}
                  className={({ isActive }) =>
                    `px-3 py-2 text-sm font-medium rounded-md transition-colors ${isActive ? 'text-fern-600 bg-sand-100' : 'text-gray-700 hover:text-fern-600 hover:bg-sand-50'}`
                  }
                >
                  {item.label}
                </NavLink>
              ),
            )}
          </nav>

          <div className="flex items-center gap-2">
            <HeaderAuthLink className="hidden sm:inline-flex items-center rounded-md bg-fern-600 px-4 py-2 text-sm font-semibold text-white hover:bg-fern-700" />
            <button
              type="button"
              onClick={() => setMobileOpen(o => !o)}
              className="md:hidden rounded-md p-2 text-gray-700 hover:bg-sand-50"
              aria-label="Toggle navigation menu"
              aria-expanded={mobileOpen}
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
        </div>

        {mobileOpen && (
          <nav aria-label="Mobile" className="md:hidden border-t border-sand-200 bg-white px-4 py-2">
            {navItems.map((item) =>
              item.kind === 'dropdown' ? (
                <div key={item.label} className="py-1">
                  <NavLink
                    to={item.path}
                    onClick={() => setMobileOpen(false)}
                    className={({ isActive }) =>
                      `block px-3 py-2 text-sm font-semibold rounded-md ${isActive ? 'text-fern-600 bg-sand-100' : 'text-gray-700'}`
                    }
                  >
                    {item.label}
                  </NavLink>
                  <ul className="ml-3 mt-1 border-l border-sand-200">
                    {item.children.map((child) => (
                      <li key={child.path}>
                        <NavLink
                          to={child.path}
                          onClick={() => setMobileOpen(false)}
                          className={({ isActive }) =>
                            `block pl-3 pr-3 py-1.5 text-sm rounded-md ${isActive ? 'text-fern-600' : 'text-gray-600 hover:text-fern-600 hover:bg-sand-50'}`
                          }
                        >
                          {child.label}
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <NavLink
                  key={item.path}
                  to={item.path}
                  end={item.end}
                  onClick={() => setMobileOpen(false)}
                  className={({ isActive }) =>
                    `block px-3 py-2 text-sm font-medium rounded-md ${isActive ? 'text-fern-600 bg-sand-100' : 'text-gray-700 hover:bg-sand-50'}`
                  }
                >
                  {item.label}
                </NavLink>
              ),
            )}
            <HeaderAuthLink
              className="mt-2 block rounded-md bg-fern-600 px-3 py-2 text-center text-sm font-semibold text-white hover:bg-fern-700"
              onClick={() => setMobileOpen(false)}
            />
          </nav>
        )}
      </header>

      <main id="main" className="flex-1">
        <Outlet />
      </main>

      <footer className="bg-fern-600 text-sand-100">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-10 md:grid-cols-4">
            <div className="md:col-span-2">
              <img
                src="/brand/conscierra-logo-white.png"
                alt="Conscierra"
                className="h-10 w-auto"
                width={183}
                height={59}
              />
              <p className="mt-4 max-w-md text-sm text-sand-100/90">
                {region.footerTagline}
              </p>
              <p className="mt-4 text-xs text-sand-200/70">
                Conscierra is a trading name of {region.legalEntity}.
              </p>
            </div>

            <div>
              <h2 className="text-sm font-semibold text-white uppercase tracking-wide">Site</h2>
              <ul className="mt-3 space-y-2 text-sm">
                <li><Link to="/about" className="text-sand-100/80 hover:text-white">About</Link></li>
                <li><Link to="/pricing" className="text-sand-100/80 hover:text-white">Pricing</Link></li>
                <li><Link to="/contact" className="text-sand-100/80 hover:text-white">Contact</Link></li>
                <li><Link to="/privacy" className="text-sand-100/80 hover:text-white">Privacy</Link></li>
                <li><Link to="/cookies" className="text-sand-100/80 hover:text-white">Cookies</Link></li>
                <li><Link to="/terms" className="text-sand-100/80 hover:text-white">Terms of Use</Link></li>
                <li>
                  <button
                    type="button"
                    onClick={openCookiePreferences}
                    className="text-left text-sand-100/80 hover:text-white"
                  >
                    Cookie preferences
                  </button>
                </li>
                <li><Link to="/login" className="text-sand-100/80 hover:text-white">Client login</Link></li>
              </ul>
            </div>

            <div>
              <h2 className="text-sm font-semibold text-white uppercase tracking-wide">Get in touch</h2>
              <address className="not-italic mt-3 space-y-2 text-sm">
                <p className="text-sand-100/80">
                  {region.address.lines.map((line, i) => (
                    <span key={i}>
                      {line}
                      {i < region.address.lines.length - 1 && <br />}
                    </span>
                  ))}
                </p>
                <p>
                  {region.phone.tel ? (
                    <a href={`tel:${region.phone.tel}`} className="text-sand-100/80 hover:text-white">
                      {region.phone.display}
                    </a>
                  ) : (
                    <span className="text-sand-100/80">{region.phone.display}</span>
                  )}
                </p>
                <p>
                  <a href={`mailto:${region.email}`} className="text-sand-100/80 hover:text-white">
                    {region.email}
                  </a>
                </p>
                <p className="text-sand-200/60 text-xs pt-1">Office hours: {region.hours}</p>
              </address>
            </div>
          </div>

          <div className="mt-10 flex flex-col gap-2 border-t border-fern-500/40 pt-6 text-xs text-sand-200/70 sm:flex-row sm:items-center sm:justify-between">
            <p>© {new Date().getFullYear()} {region.legalEntity}. All rights reserved.</p>
            <p>{region.complianceBadge}</p>
          </div>
        </div>
      </footer>

      <CookieConsentMount />
    </div>
  );
}

/**
 * Desktop dropdown for the Services menu. Hover opens; focus-within keeps it
 * open for keyboard users; Escape closes; outside click closes. The parent
 * label is a NavLink to the dropdown's `path` (/services overview) so a
 * keyboard or mouse activation still navigates rather than just expanding.
 */
function NavDropdown({ item }: { item: DropdownNavItem }) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const location = useLocation();
  const parentActive =
    location.pathname === item.path || location.pathname.startsWith(`${item.path}/`);

  // Close on route change.
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  // Outside click + Escape close.
  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const childActive = item.children.some((c) => location.pathname.startsWith(c.path));
  const highlight = parentActive || childActive;

  return (
    <div
      ref={wrapperRef}
      className="relative"
      onBlur={(e) => {
        // Only close if focus is leaving the wrapper entirely.
        if (!wrapperRef.current?.contains(e.relatedTarget as Node)) {
          setOpen(false);
        }
      }}
    >
      <div className="flex items-center">
        <NavLink
          to={item.path}
          className={
            'px-3 py-2 text-sm font-medium rounded-md transition-colors ' +
            (highlight
              ? 'text-fern-600 bg-sand-100'
              : 'text-gray-700 hover:text-fern-600 hover:bg-sand-50')
          }
        >
          {item.label}
        </NavLink>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label={`Toggle ${item.label} menu`}
          className={
            '-ml-1 px-1.5 py-2 text-sm rounded-md transition-colors ' +
            (highlight
              ? 'text-fern-600 bg-sand-100'
              : 'text-gray-700 hover:text-fern-600 hover:bg-sand-50')
          }
        >
          <svg
            className={`h-3.5 w-3.5 transition-transform ${open ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>

      {open && (
        <ul
          role="menu"
          aria-label={`${item.label} submenu`}
          className="absolute right-0 z-50 mt-1 min-w-[16rem] rounded-lg border border-sand-200 bg-white py-1 shadow-lg"
        >
          {item.children.map((child) => (
            <li key={child.path} role="none">
              <NavLink
                to={child.path}
                role="menuitem"
                className={({ isActive }) =>
                  `block px-4 py-2 text-sm transition-colors ${
                    isActive
                      ? 'text-fern-600 bg-sand-50'
                      : 'text-gray-700 hover:text-fern-600 hover:bg-sand-50'
                  }`
                }
              >
                {child.label}
              </NavLink>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
